/**
 * Utilitaires pour construire les URLs des assets statiques (thumbnails, vidéos)
 * Le backend exige un JWT pour /videos/ et /thumbnails/ (header ou ?token=)
 */

const PROTECTED_MEDIA_PREFIXES = ['/videos/', '/thumbnails/'];
const MEDIA_MARKERS = ['/videos/', '/thumbnails/'];

/**
 * Obtient l'URL de base pour les assets statiques
 */
const getBaseUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  if (apiUrl.includes('/api')) {
    return apiUrl.replace('/api', '');
  }

  return apiUrl.replace(/\/api$/, '') || 'http://localhost:5000';
};

const isProtectedMediaUrl = (url) => {
  if (!url) return false;
  return PROTECTED_MEDIA_PREFIXES.some((prefix) => url.includes(prefix));
};

const getAuthToken = (authToken) => {
  if (authToken) {
    return authToken;
  }
  return localStorage.getItem('token');
};

const appendAuthToken = (url, authToken) => {
  if (!url || !isProtectedMediaUrl(url)) {
    return url;
  }

  const token = getAuthToken(authToken);
  if (!token) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
};

/**
 * Convertit un chemin disque ou relatif en chemin web (/videos/... ou /thumbnails/...)
 * Gère les chemins absolus (/opt/video-platform/videos/...) et encode les caractères spéciaux
 */
export const normalizeMediaPath = (inputPath) => {
  if (!inputPath) {
    return null;
  }

  let path = String(inputPath).replace(/\\/g, '/');

  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      const url = new URL(path);
      path = url.pathname + (url.search || '');
    } catch {
      return null;
    }
  }

  let matched = false;
  for (const marker of MEDIA_MARKERS) {
    const idx = path.lastIndexOf(marker);
    if (idx !== -1) {
      path = path.substring(idx);
      matched = true;
      break;
    }
  }

  if (!matched) {
    path = path.replace(/^\.\//, '').replace(/^\/+/, '');
    if (path.startsWith('videos/') || path.startsWith('thumbnails/')) {
      path = `/${path}`;
    } else if (path) {
      path = `/videos/${path}`;
    }
  }

  if (!path.startsWith('/videos/') && !path.startsWith('/thumbnails/')) {
    return null;
  }

  const qIndex = path.indexOf('?');
  const pathname = qIndex === -1 ? path : path.slice(0, qIndex);
  const search = qIndex === -1 ? '' : path.slice(qIndex);

  const encodedPath = pathname
    .split('/')
    .map((segment) => (segment ? encodeURIComponent(decodeURIComponent(segment)) : segment))
    .join('/');

  return encodedPath + search;
};

/**
 * Construit l'URL complète d'un asset (thumbnail, vidéo, etc.)
 */
export const getAssetUrl = (relativePath, authToken) => {
  const normalized = normalizeMediaPath(relativePath);
  if (!normalized) {
    return null;
  }

  const url = `${getBaseUrl()}${normalized}`;
  return appendAuthToken(url, authToken);
};

export const getThumbnailUrl = (thumbnailPath, authToken) => {
  return getAssetUrl(thumbnailPath, authToken);
};

export const getVideoUrl = (videoPath, authToken) => {
  return getAssetUrl(videoPath, authToken);
};

/**
 * Résout l'URL de lecture à partir des métadonnées vidéo API
 */
export const resolveVideoUrl = (video, authToken) => {
  if (!video) {
    return null;
  }

  if (video.streamUrl) {
    return getVideoUrl(video.streamUrl, authToken);
  }

  if (video.qualities?.length) {
    const preferredQuality =
      video.qualities.find((q) => q.quality === '720p') || video.qualities[0];
    return getVideoUrl(preferredQuality.path, authToken);
  }

  return null;
};

export default {
  getAssetUrl,
  getThumbnailUrl,
  getVideoUrl,
  normalizeMediaPath,
  resolveVideoUrl
};
